import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config";
import { Gift, Copy, CheckCircle2, Clock } from "lucide-react";

const STORAGE_KEY = "trp_switcher_coupon";

function useCountdown(targetIso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!targetIso) return null;
  const remain = Math.max(0, new Date(targetIso).getTime() - now);
  if (remain === 0) return { d: 0, h: 0, m: 0, s: 0, total: 0, urgent: true };
  const d = Math.floor(remain / 86_400_000);
  const h = Math.floor((remain % 86_400_000) / 3_600_000);
  const m = Math.floor((remain % 3_600_000) / 60_000);
  const s = Math.floor((remain % 60_000) / 1000);
  return { d, h, m, s, total: remain, urgent: remain < 3_600_000 };
}

function pad(n) { return String(n).padStart(2, "0"); }

/**
 * V186 — Switcher coupon banner with FOMO countdown timer.
 *
 * Sliding-window expiry: every fresh page-load issues a new `expires_at`
 * = now + valid_days × 86400s. Stored in localStorage. The visible
 * countdown turns red <1h. Once it hits zero we still display the
 * banner but mark it expired (the user can re-claim by reloading).
 */
export default function SwitcherCouponBanner({ slug }) {
  const [coupon, setCoupon] = useState(null);
  const [copied, setCopied] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    const ctrl = new AbortController();
    axios.get(`${API}/switcher-coupon/issue/${slug}`, { signal: ctrl.signal })
      .then(r => {
        if (cancelled) return;
        const issuedAt = new Date();
        const validDays = r.data?.valid_days || 14;
        const expiresAt = new Date(issuedAt.getTime() + validDays * 86_400_000);
        const enriched = {
          ...r.data,
          issued_at: issuedAt.toISOString(),
          expires_at: expiresAt.toISOString(),
        };
        setCoupon(enriched);
        try {
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify(enriched));
        } catch {}
      })
      .catch(() => {});
    const t = setTimeout(() => setVisible(true), 5000);
    return () => { cancelled = true; ctrl.abort(); clearTimeout(t); };
  }, [slug]);

  const cd = useCountdown(coupon?.expires_at);

  const copy = async () => {
    if (!coupon?.code) return;
    try {
      await navigator.clipboard.writeText(coupon.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {}
  };

  if (!coupon || !visible) return null;

  const expired = cd && cd.total === 0;
  const urgent = cd && cd.urgent && !expired;

  // Color tier: urgent → red; expired → gray; else emerald
  const wrapClass = expired
    ? "from-gray-400 via-gray-300 to-gray-200 text-gray-800 shadow-[0_12px_40px_rgba(75,85,99,0.3)]"
    : urgent
      ? "from-rose-500 via-rose-400 to-orange-400 text-rose-950 shadow-[0_12px_40px_rgba(225,29,72,0.45)]"
      : "from-emerald-500 via-emerald-400 to-teal-400 text-emerald-950 shadow-[0_12px_40px_rgba(16,185,129,0.45)]";

  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 max-w-[94vw]"
      data-testid="switcher-coupon-banner"
    >
      <div className={`bg-gradient-to-r ${wrapClass} rounded-2xl border border-white/30 backdrop-blur px-4 py-3 flex items-center gap-3 flex-wrap`}>
        <div className="flex items-center gap-2">
          <div className="bg-white/30 rounded-full p-1.5">
            <Gift className="w-4 h-4" />
          </div>
          <div className="leading-tight">
            <div className="text-[10px] tracking-[0.25em] uppercase font-extrabold opacity-80">
              {coupon.label}
            </div>
            <div className="font-mono font-black text-base lg:text-lg" data-testid="switcher-coupon-code">
              {coupon.code}
            </div>
          </div>
        </div>

        {/* Countdown — replaces static "14-day" with live timer */}
        {cd && !expired && (
          <div className="flex items-center gap-1 text-xs font-extrabold" data-testid="switcher-coupon-countdown">
            <Clock className={`w-3.5 h-3.5 ${urgent ? "animate-pulse" : ""}`} />
            {cd.d > 0 ? (
              <span>Expires in <span className="tabular-nums">{cd.d}d {pad(cd.h)}:{pad(cd.m)}:{pad(cd.s)}</span></span>
            ) : (
              <span>Expires in <span className="tabular-nums">{pad(cd.h)}:{pad(cd.m)}:{pad(cd.s)}</span></span>
            )}
          </div>
        )}
        {expired && (
          <div className="text-xs font-bold opacity-75" data-testid="switcher-coupon-expired">Expired — reload to claim</div>
        )}

        <button
          type="button"
          onClick={copy}
          disabled={expired}
          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-extrabold transition-colors ${
            expired ? "bg-gray-500/40 cursor-not-allowed" : copied ? "bg-emerald-900 text-emerald-100" : "bg-black/90 text-white hover:bg-black"
          }`}
          data-testid="switcher-coupon-copy"
        >
          {copied ? <><CheckCircle2 className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
        </button>

        {!expired && (
          <a
            href="/register"
            className="inline-flex items-center gap-1.5 bg-black/90 text-white hover:bg-black px-3 py-2 rounded-lg text-xs font-extrabold transition-colors"
            data-testid="switcher-coupon-redeem"
          >
            Redeem now →
          </a>
        )}

        <button
          type="button"
          onClick={() => setVisible(false)}
          className="opacity-60 hover:opacity-100 text-xs font-bold px-2"
          data-testid="switcher-coupon-dismiss"
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
